/**
 * ProjectBids — staff screen to bid a project's labor out to contractors.
 *
 * Shows the suggested offer (75% of the project's labor), lets the owner pick
 * one or more active contractors and adjust each offer, and sends. Also lists
 * existing bids with their status. Multiple concurrent bids are allowed.
 * Staff-only. See .kiro/specs/contractor-bids.md.
 */
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  getProjectBids,
  sendProjectBids,
  getContractors,
  getMe,
  assignBid,
  rejectCounter,
  type ProjectBidsView,
  type ProjectBid,
  type Contractor,
} from '../../lib/api';

function dollars(cents: number): string {
  return `$${(cents / 100).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function pct(fraction: number): string {
  return `${(fraction * 100).toFixed(1)}%`;
}

/** Statuses where the owner can still assign this bid (confirm the subcontract). */
const ASSIGNABLE = new Set(['OFFERED', 'ACCEPTED_BY_CONTRACTOR', 'COUNTERED']);

const STATUS_LABEL: Record<string, string> = {
  OFFERED: 'Offered — awaiting response',
  ACCEPTED_BY_CONTRACTOR: 'Contractor accepted — assign to confirm',
  COUNTERED: 'Countered — needs your decision',
  ASSIGNED: 'Assigned',
  DECLINED: 'Declined',
  EXPIRED: 'Expired',
  CLOSED_OTHER_ASSIGNED: 'Closed (assigned elsewhere)',
};

export function ProjectBids(): JSX.Element {
  const navigate = useNavigate();
  const { projectId } = useParams<{ projectId: string }>();
  const [allowed, setAllowed] = useState<boolean | null>(null);
  const [view, setView] = useState<ProjectBidsView | null>(null);
  const [contractors, setContractors] = useState<Contractor[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [sending, setSending] = useState(false);
  // Bid id currently being acted on (assign / reject-counter), for disabling buttons.
  const [acting, setActing] = useState<string | null>(null);

  // Selected contractor ids → adjusted offer (dollars string; empty = default).
  const [picked, setPicked] = useState<Record<string, string>>({});

  useEffect(() => {
    getMe()
      .then(async (me) => {
        const ok = me.role === 'ADMINISTRATOR' || me.role === 'ESTIMATOR';
        setAllowed(ok);
        if (ok && projectId) {
          const [v, cs] = await Promise.all([getProjectBids(projectId), getContractors(true)]);
          setView(v);
          setContractors(cs);
        }
      })
      .catch(() => {
        setAllowed(false);
        setError('We couldn\u2019t load this project\u2019s bids.');
      });
  }, [projectId]);

  async function refresh(): Promise<void> {
    if (projectId) setView(await getProjectBids(projectId));
  }

  async function assign(bidId: string): Promise<void> {
    if (!projectId) return;
    setActing(bidId);
    setError(null);
    try {
      await assignBid(projectId, bidId);
      await refresh();
    } catch {
      setError('We couldn\u2019t assign that bid. It may no longer be assignable \u2014 reload and try again.');
    } finally {
      setActing(null);
    }
  }

  async function declineCounter(bidId: string): Promise<void> {
    if (!projectId) return;
    setActing(bidId);
    setError(null);
    try {
      await rejectCounter(projectId, bidId);
      await refresh();
    } catch {
      setError('We couldn\u2019t reject that counter. Reload and try again.');
    } finally {
      setActing(null);
    }
  }

  function togglePick(id: string): void {
    setPicked((p) => {
      const next = { ...p };
      if (id in next) delete next[id];
      else next[id] = '';
      return next;
    });
  }

  async function send(): Promise<void> {
    if (!projectId || !view) return;
    const bids = Object.entries(picked).map(([contractorId, dollarsStr]) => {
      const trimmed = dollarsStr.trim();
      const cents = trimmed === '' ? undefined : Math.round(Number(trimmed) * 100);
      return cents && !Number.isNaN(cents)
        ? { contractorId, offeredAmountCents: cents }
        : { contractorId };
    });
    if (bids.length === 0) return;
    setSending(true);
    setError(null);
    try {
      await sendProjectBids(projectId, bids);
      setPicked({});
      await refresh();
    } catch {
      setError('We couldn\u2019t send those bids. Try again.');
    } finally {
      setSending(false);
    }
  }

  if (allowed === null) {
    return <div className="flow flow--single"><div className="stage-card"><p className="stage-card__lede">Checking access&hellip;</p></div></div>;
  }
  if (!allowed) {
    return (
      <div className="flow flow--single"><div className="stage-card">
        <h1>Not available</h1>
        <div className="q-actions"><button type="button" className="pb-btn" onClick={() => navigate('/dashboard')}>Back</button></div>
      </div></div>
    );
  }
  if (!view) {
    return <div className="flow flow--single"><div className="stage-card"><p className="stage-card__lede">Loading&hellip;</p></div></div>;
  }

  const pickedIds = new Set(Object.keys(picked));

  return (
    <div className="flow flow--single">
      <div className="stage-card">
        <div className="progress__count">BID THE LABOR OUT</div>
        <h1>Contractor bids</h1>
        <p className="stage-card__lede">
          Suggested offer is <strong>{dollars(view.suggestedOfferCents)}</strong> — 75% of the
          project&rsquo;s labor ({dollars(view.laborCents)}). Adjust per contractor before sending.
        </p>

        {!view.customerAccepted ? (
          <div className="pb-note pb-note--flag" role="note">
            The customer hasn&rsquo;t accepted the estimate yet — you can still line up a contractor,
            just know it isn&rsquo;t a confirmed job.
          </div>
        ) : null}

        {/* Existing bids */}
        {view.bids.length > 0 ? (
          <div style={{ marginTop: 'var(--pb-3)' }}>
            <div className="progress__count" style={{ marginBottom: 'var(--pb-2)' }}>BIDS SENT</div>
            <p className="estimate__hint" style={{ marginBottom: 'var(--pb-2)' }}>
              Sale price {dollars(view.priceCents)} &middot; materials {dollars(view.materialsCents)}.
              Gross &amp; margin below are what you keep after materials and the contractor&rsquo;s amount.
            </p>
            {view.bids.map((b) => (
              <BidCard
                key={b.id}
                bid={b}
                acting={acting === b.id}
                onAssign={() => void assign(b.id)}
                onRejectCounter={() => void declineCounter(b.id)}
              />
            ))}
          </div>
        ) : null}

        {/* Pick contractors to send to */}
        <div style={{ marginTop: 'var(--pb-4)' }}>
          <div className="progress__count" style={{ marginBottom: 'var(--pb-2)' }}>SEND A BID</div>
          {contractors.length === 0 ? (
            <p className="estimate__hint">
              No active contractors.{' '}
              <button type="button" className="progress__back" onClick={() => navigate('/admin/manage-contractors')}>
                Add one
              </button>
            </p>
          ) : (
            contractors.map((c) => {
              const isPicked = pickedIds.has(c.id);
              return (
                <div key={c.id} className="pb-surface" style={{ marginBottom: 'var(--pb-2)' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--pb-2)' }}>
                    <button
                      type="button"
                      className="pb-option"
                      role="checkbox"
                      aria-checked={isPicked}
                      onClick={() => togglePick(c.id)}
                      style={{ flexGrow: 1, textAlign: 'left' }}
                    >
                      <span
                        className="pb-option__mark"
                        style={isPicked ? { background: 'var(--pb-chalk)', borderColor: 'var(--pb-chalk)', color: '#fff' } : undefined}
                      />
                      <span style={{ flexGrow: 1 }}>
                        <span className="pb-option__label">{c.name}{c.trade ? ` — ${c.trade}` : ''}</span>
                        <span className="pb-option__sub">{c.email ?? 'no email on file'}</span>
                      </span>
                    </button>
                    {isPicked ? (
                      <input
                        className="pb-field"
                        style={{ width: '7rem' }}
                        inputMode="decimal"
                        placeholder={(view.suggestedOfferCents / 100).toFixed(2)}
                        value={picked[c.id] ?? ''}
                        onChange={(e) => setPicked((p) => ({ ...p, [c.id]: e.target.value }))}
                        aria-label={`Offer for ${c.name}`}
                      />
                    ) : null}
                  </div>
                </div>
              );
            })
          )}
        </div>

        {error ? <div className="pb-note pb-note--flag" role="alert">{error}</div> : null}

        <div className="q-actions" style={{ flexDirection: 'column', marginTop: 'var(--pb-3)' }}>
          <button
            type="button"
            className="pb-btn pb-btn--lg"
            onClick={() => void send()}
            disabled={sending || pickedIds.size === 0}
          >
            {sending ? 'Sending\u2026' : `Send ${pickedIds.size || ''} bid${pickedIds.size === 1 ? '' : 's'}`.trim()}
          </button>
          <button type="button" className="pb-btn pb-btn--ghost" onClick={() => navigate('/admin/leads')}>
            Back to the queue
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * One bid row. Shows the amount in play (accepted ?? counter ?? offered), the
 * gross + margin that amount implies, and the owner's decision buttons:
 *  - OFFERED / ACCEPTED_BY_CONTRACTOR → "Assign" (confirm the subcontract).
 *  - COUNTERED → offer-vs-counter side-by-side, "Accept counter" (assign at the
 *    counter amount) or "Reject counter" (declines the bid).
 * Terminal statuses (ASSIGNED, DECLINED, EXPIRED, CLOSED_OTHER_ASSIGNED) are read-only.
 */
function BidCard({
  bid,
  acting,
  onAssign,
  onRejectCounter,
}: {
  bid: ProjectBid;
  acting: boolean;
  onAssign: () => void;
  onRejectCounter: () => void;
}): JSX.Element {
  const amount = bid.acceptedAmountCents ?? bid.counterAmountCents ?? bid.offeredAmountCents;
  const marginTone = bid.grossCents < 0 ? { color: 'var(--pb-flag, #b45309)' } : undefined;
  const isCountered = bid.status === 'COUNTERED';

  return (
    <div className="pb-surface" style={{ marginBottom: 'var(--pb-2)' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
        <span className="pb-option__label">{bid.contractorName}</span>
        <span className="pb-option__label">{dollars(amount)}</span>
      </div>
      <div className="estimate__hint">{STATUS_LABEL[bid.status] ?? bid.status}</div>

      {/* Offer vs counter, side by side, when the contractor countered. */}
      {isCountered && bid.counterAmountCents != null ? (
        <div
          style={{
            display: 'flex',
            gap: 'var(--pb-3)',
            marginTop: 'var(--pb-2)',
            fontSize: '0.9em',
          }}
        >
          <span>
            <span className="pb-option__sub">You offered</span>
            <br />
            {dollars(bid.offeredAmountCents)}
          </span>
          <span>
            <span className="pb-option__sub">They want</span>
            <br />
            <strong>{dollars(bid.counterAmountCents)}</strong>
          </span>
          <span>
            <span className="pb-option__sub">Difference</span>
            <br />
            +{dollars(bid.counterAmountCents - bid.offeredAmountCents)}
          </span>
        </div>
      ) : null}

      {/* Gross + margin this amount implies. */}
      <div className="estimate__hint" style={{ marginTop: 'var(--pb-1)', ...marginTone }}>
        If assigned at {dollars(amount)}: gross {dollars(bid.grossCents)} &middot; margin {pct(bid.marginPct)}
      </div>

      {/* Decision buttons. */}
      {ASSIGNABLE.has(bid.status) ? (
        <div className="q-actions" style={{ marginTop: 'var(--pb-2)' }}>
          <button type="button" className="pb-btn" onClick={onAssign} disabled={acting}>
            {acting ? 'Working\u2026' : isCountered ? 'Accept counter & assign' : 'Assign'}
          </button>
          {isCountered ? (
            <button type="button" className="pb-btn pb-btn--ghost" onClick={onRejectCounter} disabled={acting}>
              Reject counter
            </button>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
